import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { departmentsApi } from '@/api/departments'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Loader2, Upload, Trash2, ZoomIn } from 'lucide-react'
import { toast } from 'sonner'
import { showError } from '@/lib/error'

export function SettlementQrcodeSettings() {
  const queryClient = useQueryClient()
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({})
  const [uploadingId, setUploadingId] = useState<number | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const { data: departments = [], isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.getTree(),
  })

  // 过滤顶级部门
  const topDepartments = (departments ?? []).filter(d => d.parent_id === null)

  const qrcodeMutation = useMutation({
    mutationFn: ({ id, url }: { id: number; url: string }) =>
      departmentsApi.updateQrcode(id, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      toast.success('收款码已更新')
    },
    onError: (error) => showError(error, '收款码更新失败'),
  })

  const handleUpload = async (deptId: number, file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('文件大小不能超过5MB')
      return
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
    if (!allowedTypes.includes(file.type)) {
      toast.error('仅支持 jpg/png 格式')
      return
    }

    setUploadingId(deptId)
    try {
      const url = await authApi.uploadQrcode(file)
      qrcodeMutation.mutate({ id: deptId, url })
    } catch (error) {
      showError(error, '上传失败')
    } finally {
      setUploadingId(null)
    }
  }

  const handleDelete = (deptId: number) => {
    qrcodeMutation.mutate({ id: deptId, url: '' })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (topDepartments.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-8">
        暂无顶级部门，请先在部门管理中创建
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        为每个顶级部门设置收款码，用于结算时指定收款方
      </p>
      <div className="space-y-3">
        {topDepartments.map(dept => (
          <div
            key={dept.id}
            className="flex items-center justify-between rounded-lg border p-4"
          >
            <div className="flex items-center gap-4">
              <span className="font-medium min-w-[120px]">{dept.name}</span>
              {dept.payment_qrcode ? (
                <div className="flex items-center gap-2">
                  <img
                    src={dept.payment_qrcode}
                    alt={`${dept.name} 收款码`}
                    className="h-12 w-12 rounded border object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setPreviewUrl(dept.payment_qrcode)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPreviewUrl(dept.payment_qrcode)}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">未设置</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={el => { fileInputRefs.current[dept.id] = el }}
                type="file"
                accept=".jpg,.jpeg,.png"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0]
                  if (file) handleUpload(dept.id, file)
                  e.target.value = ''
                }}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={uploadingId === dept.id}
                onClick={() => fileInputRefs.current[dept.id]?.click()}
              >
                {uploadingId === dept.id ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-1.5 h-4 w-4" />
                )}
                {dept.payment_qrcode ? '更换' : '上传'}
              </Button>
              {dept.payment_qrcode && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={uploadingId === dept.id}
                  onClick={() => handleDelete(dept.id)}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  删除
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 图片预览弹窗 */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-md">
          <DialogTitle>收款码预览</DialogTitle>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="收款码预览"
              className="w-full rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
